import { Button } from '@base-ui/react/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { ParseKeys } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { apiErrorDetail } from '@/admin/errorDetail';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { CourseHeader } from '@/admin/ui/ContentNav';
import { DetailPage } from '@/admin/ui/Editor';
import { ErrorText } from '@/admin/ui/Form';
import type { ChangeKind } from '@/admin/ui/Versions';
import { ChangeMark, invalidateCourseVersions } from '@/admin/ui/Versions';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type Change = components['schemas']['Change'];
type ChangeSet = components['schemas']['ChangeSet'];
type Blocker = components['schemas']['Blocker'];

export const Route = createFileRoute(
  '/admin/courses/$courseId/$version_/changes',
)({
  staticData: { minTier: 'ADMIN' },
  component: ReviewChanges,
});

const ENTITY = {
  course: 'admin.courseChanges.entity.course',
  lesson: 'admin.courseChanges.entity.lesson',
  topic: 'admin.courseChanges.entity.topic',
  question: 'admin.courseChanges.entity.question',
} as const satisfies Record<Change['entity'], ParseKeys>;

// Each button says what it does to this one change.
const UNDO = {
  edited: 'admin.courseChanges.undo.edited',
  removed: 'admin.courseChanges.undo.removed',
  moved: 'admin.courseChanges.undo.moved',
  added: 'admin.courseChanges.undo.added',
} as const satisfies Record<ChangeKind, ParseKeys>;

function ReviewChanges() {
  const { t } = useTranslation();
  const { courseId, version } = Route.useParams();
  const course_id = Number(courseId);
  const versionNum = Number(version);
  const [publishing, setPublishing] = useState(false);

  const course = api.adminGetCourse.useQuery({
    path: { course_id },
    query: { version: versionNum },
  });
  const changes = api.adminCourseVersionChanges.useQuery({
    path: { course_id, version: versionNum },
  });

  if (course.isLoading || changes.isLoading)
    return <Message>{t('admin.courseChanges.loading')}</Message>;
  if (!course.data || !changes.data)
    return (
      <Message $error>
        {apiErrorDetail(changes.error) ?? t('admin.courseChanges.loadError')}
      </Message>
    );

  const set = changes.data;
  return (
    <DetailPage>
      <CourseHeader course={course.data} />
      <Heading>
        <h2>
          {t('admin.courseChanges.heading', {
            version: versionNum,
            interpolation: { escapeValue: false },
          })}
        </h2>
        <span>
          {t('admin.courseChanges.comparedWith', {
            version: set.base_version,
            interpolation: { escapeValue: false },
          })}
        </span>
      </Heading>
      <Summary set={set} />
      {set.changes.length === 0 ? (
        <Empty>{t('admin.courseChanges.noChanges')}</Empty>
      ) : (
        <Groups
          changes={set.changes}
          courseId={course_id}
          version={versionNum}
        />
      )}
      <Unchanged>
        {t('admin.courseChanges.unchanged', {
          lessons: t('admin.courseChanges.lessonCount', {
            count: set.unchanged_lessons,
            interpolation: { escapeValue: false },
          }),
          topics: t('admin.courseChanges.topicCount', {
            count: set.unchanged_topics,
            interpolation: { escapeValue: false },
          }),
          questions: t('admin.courseChanges.questionCount', {
            count: set.unchanged_questions,
            interpolation: { escapeValue: false },
          }),
          interpolation: { escapeValue: false },
        })}
      </Unchanged>
      <StickyBar>
        <span>
          {t('admin.courseChanges.publishNote', {
            version: versionNum,
            interpolation: { escapeValue: false },
          })}
        </span>
        <Button
          className={`${buttonStyles.Button} ${buttonStyles.Primary}`}
          onClick={() => setPublishing(true)}
        >
          {t('admin.courseChanges.publishButton', {
            version: versionNum,
            interpolation: { escapeValue: false },
          })}
        </Button>
      </StickyBar>
      {publishing && (
        <PublishDialog
          courseId={course_id}
          version={versionNum}
          title={course.data.title}
          onClose={() => setPublishing(false)}
        />
      )}
    </DetailPage>
  );
}

function Summary({ set }: { set: ChangeSet }) {
  const { t } = useTranslation();
  const chips: [ChangeKind, ParseKeys][] = [
    ['added', 'admin.courseChanges.chip.added'],
    ['edited', 'admin.courseChanges.chip.edited'],
    ['moved', 'admin.courseChanges.chip.moved'],
    ['removed', 'admin.courseChanges.chip.removed'],
  ];
  return (
    <Chips aria-label={t('admin.courseChanges.summary')}>
      {chips
        .filter(([kind]) => set.counts[kind] > 0)
        .map(([kind, word]) => (
          <Chip key={kind} $kind={kind}>
            <ChangeMark kind={kind} /> {set.counts[kind]} {t(word)}
          </Chip>
        ))}
    </Chips>
  );
}

// Consecutive changes under one lesson share a heading; the list is already in
// outline order.
function Groups({
  changes,
  courseId,
  version,
}: {
  changes: Change[];
  courseId: number;
  version: number;
}) {
  const { t } = useTranslation();
  const groups: { key: string; title: string; changes: Change[] }[] = [];
  for (const change of changes) {
    const title =
      change.entity === 'course'
        ? t('admin.courseChanges.entity.course')
        : change.entity === 'lesson'
          ? change.title
          : (change.lesson?.title ?? '');
    const key = `${change.entity === 'lesson' ? change.lineage_id : change.lesson?.lineage_id}`;
    const last = groups.at(-1);
    if (last && last.key === key && change.entity !== 'course')
      last.changes.push(change);
    else groups.push({ key, title, changes: [change] });
  }
  return (
    <GroupList>
      {groups.map((group, i) => (
        <Group key={`${group.key}-${i}`}>
          <GroupTitle>{group.title}</GroupTitle>
          {group.changes.map((change) => (
            <ChangeRow
              key={`${change.entity}-${change.lineage_id}-${change.kind}`}
              change={change}
              courseId={courseId}
              version={version}
            />
          ))}
        </Group>
      ))}
    </GroupList>
  );
}

function describe(change: Change): string {
  const what = i18n.t(ENTITY[change.entity]);
  switch (change.kind) {
    case 'added':
      return change.contains
        ? i18n.t('admin.courseChanges.describeNewContaining', {
            what,
            contains: change.contains,
            interpolation: { escapeValue: false },
          })
        : i18n.t('admin.courseChanges.describeNew', {
            what,
            interpolation: { escapeValue: false },
          });
    case 'removed':
      return change.contains
        ? i18n.t('admin.courseChanges.describeRemovedContaining', {
            what,
            contains: change.contains,
            interpolation: { escapeValue: false },
          })
        : i18n.t('admin.courseChanges.describeRemoved', {
            what,
            interpolation: { escapeValue: false },
          });
    case 'moved':
      return change.from_parent
        ? i18n.t('admin.courseChanges.describeMovedHere', {
            what,
            from: change.from_parent.title,
            interpolation: { escapeValue: false },
          })
        : i18n.t('admin.courseChanges.describeMovedPosition', {
            what,
            from: change.from_position,
            to: change.to_position,
            interpolation: { escapeValue: false },
          });
    default: {
      const parts = [
        change.wording?.length && i18n.t('admin.courseChanges.partWording'),
        change.settings?.length && i18n.t('admin.courseChanges.partSettings'),
        change.images?.length && i18n.t('admin.courseChanges.partImages'),
      ].filter(Boolean);
      const list =
        parts.length > 1
          ? i18n.t('admin.common.listAnd', {
              head: parts.slice(0, -1).join(', '),
              last: parts.at(-1),
              interpolation: { escapeValue: false },
            })
          : parts[0];
      return i18n.t('admin.courseChanges.describeEdited', {
        what,
        list,
        interpolation: { escapeValue: false },
      });
    }
  }
}

function ItemLink({ change }: { change: Change }) {
  const id = change.draft_id;
  if (id === null) return <s>{change.title}</s>;
  const text = <strong>{change.title}</strong>;
  if (change.entity === 'lesson')
    return (
      <Link to="/admin/lessons/$lessonId/" params={{ lessonId: String(id) }}>
        {text}
      </Link>
    );
  if (change.entity === 'topic')
    return (
      <Link to="/admin/topics/$topicId/" params={{ topicId: String(id) }}>
        {text}
      </Link>
    );
  if (change.entity === 'question')
    return (
      <Link
        to="/admin/questions/$questionId/"
        params={{ questionId: String(id) }}
      >
        {text}
      </Link>
    );
  return text;
}

function ChangeRow({
  change,
  courseId,
  version,
}: {
  change: Change;
  courseId: number;
  version: number;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const wording = change.wording ?? [];
  const settings = change.settings ?? [];
  const images = change.images ?? [];
  const revert = api.adminRevertCourseVersionChange.useMutation(undefined, {
    meta: { toast: t('admin.courseChanges.changeUndone') },
    onSuccess: (next) => {
      setConfirming(false);
      queryClient.setQueryData(
        api.adminCourseVersionChanges.getQueryKey({
          path: { course_id: courseId, version },
        }),
        next,
      );
      invalidateCourseVersions(courseId);
    },
  });
  const run = (attribute?: string) =>
    revert.mutate({
      path: { course_id: courseId, version },
      body: {
        entity: change.entity,
        lineage_id: change.lineage_id,
        kind: change.kind,
        ...(attribute ? { attribute } : {}),
      },
    });
  // With more than one field changed, each can be undone on its own. Questions
  // carry no attribute: they only revert whole.
  const perField =
    change.kind === 'edited' &&
    [...wording, ...settings, ...images].filter((f) => f.attribute).length > 1;
  const fieldUndo = (attribute: string | null | undefined, field: string) =>
    perField && attribute ? (
      <FieldUndo
        type="button"
        disabled={revert.isPending}
        aria-label={t('admin.courseChanges.revertFieldAria', {
          field,
          interpolation: { escapeValue: false },
        })}
        onClick={() => run(attribute)}
      >
        {t('admin.courseChanges.revert')}
      </FieldUndo>
    ) : null;
  return (
    <Row>
      <RowHead>
        <RowTitle>
          <ChangeMark kind={change.kind} />
          <ItemLink change={change} />
          <Kind>{describe(change)}</Kind>
        </RowTitle>
        <Button
          className={buttonStyles.Button}
          disabled={revert.isPending}
          onClick={() =>
            change.kind === 'added' ? setConfirming(true) : run()
          }
        >
          {perField ? t('admin.courseChanges.revertAll') : t(UNDO[change.kind])}
        </Button>
      </RowHead>
      {wording.map((w) => (
        <Diff key={w.field}>
          <DiffHead>
            <DiffLabel>{w.field}</DiffLabel>
            {fieldUndo(w.attribute, w.field)}
          </DiffHead>
          <p>
            {w.segments.map((s, i) =>
              s.op === 'insert' ? (
                <ins key={i}>{s.text}</ins>
              ) : s.op === 'delete' ? (
                <del key={i}>{s.text}</del>
              ) : (
                <span key={i}>{s.text}</span>
              ),
            )}
          </p>
        </Diff>
      ))}
      {settings.length > 0 && (
        <Settings>
          {settings.map((s, i) => (
            <span key={s.field}>
              {i > 0 && ' · '}
              {s.field}: <b>{s.before || t('admin.courseChanges.none')}</b> →{' '}
              <b>{s.after || t('admin.courseChanges.none')}</b>
              {fieldUndo(s.attribute, s.field)}
            </span>
          ))}
        </Settings>
      )}
      {images.map((image) => (
        <Diff key={image.field}>
          <DiffHead>
            <DiffLabel>{image.field}</DiffLabel>
            {fieldUndo(image.attribute, image.field)}
          </DiffHead>
          <ImagePair>
            <ImageSide
              label={t('admin.courseChanges.live')}
              url={image.before}
            />
            <Arrow aria-hidden="true">→</Arrow>
            <ImageSide
              label={t('admin.courseChanges.thisDraft')}
              url={image.after}
            />
          </ImagePair>
        </Diff>
      ))}
      {revert.isError && (
        <ErrorText>
          {apiErrorDetail(revert.error) ?? t('admin.courseChanges.undoError')}
        </ErrorText>
      )}
      <ConfirmDialog
        open={confirming}
        title={t('admin.courseChanges.removeTitle', {
          title: change.title,
          interpolation: { escapeValue: false },
        })}
        confirmLabel={t('admin.common.remove')}
        tone="danger"
        pending={revert.isPending}
        onConfirm={() => run()}
        onCancel={() => setConfirming(false)}
      >
        <p>
          {t('admin.courseChanges.removeBody', {
            including: change.contains
              ? t('admin.courseChanges.including', {
                  contains: change.contains,
                  interpolation: { escapeValue: false },
                })
              : '',
            interpolation: { escapeValue: false },
          })}
        </p>
      </ConfirmDialog>
    </Row>
  );
}

function ImageSide({ label, url }: { label: string; url: string }) {
  const { t } = useTranslation();
  return (
    <figure>
      {url ? (
        <img src={url} alt={label} />
      ) : (
        <NoImage>{t('admin.courseChanges.noImage')}</NoImage>
      )}
      <figcaption>{label}</figcaption>
    </figure>
  );
}

function blockerLink(
  blocker: Pick<Blocker, 'entity' | 'id'>,
  courseId: number,
  version: number,
  label = i18n.t('admin.courseChanges.fix'),
) {
  const id = String(blocker.id);
  switch (blocker.entity) {
    case 'question':
      return (
        <Link to="/admin/questions/$questionId/" params={{ questionId: id }}>
          {label}
        </Link>
      );
    case 'topic':
      return (
        <Link to="/admin/topics/$topicId/" params={{ topicId: id }}>
          {label}
        </Link>
      );
    case 'lesson':
      return (
        <Link to="/admin/lessons/$lessonId/" params={{ lessonId: id }}>
          {label}
        </Link>
      );
    default:
      return (
        <Link
          to="/admin/courses/$courseId/$version/"
          params={{ courseId: String(courseId), version: String(version) }}
        >
          {label}
        </Link>
      );
  }
}

function PublishDialog({
  courseId,
  version,
  title,
  onClose,
}: {
  courseId: number;
  version: number;
  title: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const check = api.adminCourseVersionPublishCheck.useQuery({
    path: { course_id: courseId, version },
  });
  const publish = api.adminPublishCourseVersion.useMutation(undefined, {
    meta: { toast: t('admin.courseChanges.published') },
    onSuccess: () => {
      invalidateCourseVersions(courseId);
      onClose();
      navigate({
        to: '/admin/courses/$courseId/$version/',
        params: { courseId: String(courseId), version: String(version) },
      });
    },
  });
  const blockers = check.data?.blockers ?? [];
  const impact = check.data?.impact ?? [];
  const hidden = check.data?.hidden ?? [];
  return (
    <ConfirmDialog
      open
      title={t('admin.courseChanges.publishTitle', {
        title,
        version,
        interpolation: { escapeValue: false },
      })}
      confirmLabel={t('admin.courseChanges.publishButtonShort', {
        version,
        interpolation: { escapeValue: false },
      })}
      cancelLabel={t('admin.courseChanges.backToChanges')}
      tone="primary"
      pending={publish.isPending || check.isLoading || blockers.length > 0}
      onConfirm={() =>
        publish.mutate({ path: { course_id: courseId, version } })
      }
      onCancel={onClose}
    >
      {check.isLoading ? (
        <p>{t('admin.courseChanges.checking')}</p>
      ) : (
        <>
          <Impact>
            <li>
              <b>→</b>
              <span>
                <strong>{t('admin.courseChanges.newStudents')}</strong>{' '}
                {t('admin.courseChanges.startOn', {
                  version,
                  interpolation: { escapeValue: false },
                })}
              </span>
            </li>
            {impact.map((v) => (
              <li key={v.version}>
                <b>{v.in_progress}</b>
                <span>
                  {t('admin.courseChanges.studentNoun', {
                    count: v.in_progress,
                  })}{' '}
                  {t('admin.courseChanges.partwayThrough')}{' '}
                  <strong>
                    {t('admin.courseChanges.versionTag', {
                      version: v.version,
                      interpolation: { escapeValue: false },
                    })}
                  </strong>{' '}
                  {t('admin.courseChanges.stayOn', {
                    version: v.version,
                    interpolation: { escapeValue: false },
                  })}
                </span>
              </li>
            ))}
            <li>
              <b>0</b>
              <span>{t('admin.courseChanges.nothingChanges')}</span>
            </li>
          </Impact>
          {blockers.length > 0 && (
            <Blockers role="alert">
              <strong>
                {t('admin.courseChanges.fixThings', {
                  count: blockers.length,
                  interpolation: { escapeValue: false },
                })}
              </strong>
              <ul>
                {blockers.map((b) => (
                  <li key={`${b.code}-${b.entity}-${b.id}`}>
                    {b.title ? <em>{b.title}</em> : null} {b.message}{' '}
                    {blockerLink(b, courseId, version)}
                  </li>
                ))}
              </ul>
            </Blockers>
          )}
          {hidden.length > 0 && (
            <Hidden>
              <strong>{t('admin.courseChanges.hiddenHeading')}</strong>
              <ul>
                {hidden.map((h) => (
                  <li key={`${h.entity}-${h.id}`}>
                    <em>{h.title}</em> ({h.entity}){' '}
                    {blockerLink(
                      h,
                      courseId,
                      version,
                      t('admin.courseChanges.open'),
                    )}
                  </li>
                ))}
              </ul>
              <p>
                {t('admin.courseChanges.publishFirst', {
                  version,
                  interpolation: { escapeValue: false },
                })}
              </p>
            </Hidden>
          )}
          {publish.isError && (
            <ErrorText>
              {apiErrorDetail(publish.error) ??
                t('admin.courseChanges.publishError')}
            </ErrorText>
          )}
        </>
      )}
    </ConfirmDialog>
  );
}

const KIND_TONE: Record<ChangeKind, string> = {
  added: 'var(--success)',
  edited: 'var(--orange)',
  moved: 'var(--blue)',
  removed: 'var(--error)',
};

const Heading = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  margin-bottom: 0.5rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
  }

  span {
    color: var(--grey-700);
    font-size: 0.875rem;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: var(--component-margin);
`;

const Chip = styled.span<{ $kind: ChangeKind }>`
  align-items: center;
  background: color-mix(in srgb, ${(p) => KIND_TONE[p.$kind]} 12%, transparent);
  border-radius: 0.25rem;
  color: var(--grey-1000);
  display: inline-flex;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
`;

const Empty = styled.p`
  color: var(--grey-700);
  padding: 1rem 0;
`;

const GroupList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const Group = styled.section`
  background: var(--component-color);
  border: 1px solid var(--grey-200);
  border-radius: 0.375rem;
`;

const GroupTitle = styled.h3`
  background: var(--grey-100);
  border-bottom: 1px solid var(--grey-200);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0;
  padding: 0.375rem 0.75rem;
`;

const Row = styled.div`
  border-top: 1px solid var(--grey-150);
  display: grid;
  gap: 0.375rem;
  padding: 0.625rem 0.75rem;

  &:first-of-type {
    border-top: 0;
  }
`;

const RowHead = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

const RowTitle = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  a {
    color: var(--grey-1000);
  }
`;

const Kind = styled.span`
  color: var(--grey-700);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Diff = styled.div`
  background: var(--grey-50);
  border-radius: 0.25rem;
  font-size: 0.875rem;
  line-height: 1.6;
  padding: 0.375rem 0.625rem;

  p {
    margin: 0;
  }

  del {
    background: color-mix(in srgb, var(--error) 14%, transparent);
    color: var(--error);
  }

  ins {
    background: color-mix(in srgb, var(--success) 16%, transparent);
    color: var(--grey-1000);
    text-decoration: none;
  }
`;

const DiffHead = styled.div`
  align-items: baseline;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

// Quiet beside the row's own button: it undoes just one field.
const FieldUndo = styled.button`
  background: none;
  border: 0;
  color: var(--blue);
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
  padding: 0;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: var(--grey-500);
    cursor: default;
  }
`;

const DiffLabel = styled.div`
  color: var(--grey-700);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ImagePair = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 0.375rem;

  figure {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin: 0;
  }

  img {
    background-color: var(--grey-100);
    border: 1px solid var(--grey-200);
    border-radius: 4px;
    display: block;
    height: 90px;
    object-fit: cover;
    width: 160px;
  }

  figcaption {
    color: var(--grey-700);
    font-size: 0.75rem;
  }
`;

const NoImage = styled.div`
  align-items: center;
  border: 1px dashed var(--grey-300);
  border-radius: 4px;
  color: var(--grey-600);
  display: flex;
  font-size: 0.75rem;
  height: 90px;
  justify-content: center;
  width: 160px;
`;

const Arrow = styled.span`
  color: var(--grey-600);
  font-size: 1.25rem;
`;

const Settings = styled.p`
  color: var(--grey-800);
  font-size: 0.8125rem;
  margin: 0;

  b {
    color: var(--grey-1000);
  }
`;

const Unchanged = styled.p`
  color: var(--grey-700);
  font-size: 0.8125rem;
  margin: 0.75rem 0 5rem;
`;

const StickyBar = styled.div`
  align-items: center;
  background: var(--component-color);
  border-top: 1px solid var(--grey-200);
  bottom: 0;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  position: sticky;

  span {
    color: var(--grey-700);
    font-size: 0.8125rem;
  }
`;

const Impact = styled.ul`
  display: grid;
  gap: 0.375rem;
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;

  li {
    align-items: baseline;
    display: flex;
    gap: 0.5rem;
  }

  b {
    font-variant-numeric: tabular-nums;
    min-width: 3ch;
    text-align: right;
  }
`;

const Blockers = styled.div`
  background: color-mix(in srgb, var(--error) 8%, transparent);
  border-left: 3px solid var(--error);
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;

  ul {
    margin: 0.25rem 0 0;
    padding-left: 1.125rem;
  }
`;

const Hidden = styled.div`
  background: var(--grey-50);
  border-left: 3px solid var(--grey-400);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;

  ul {
    margin: 0.25rem 0;
    padding-left: 1.125rem;
  }

  p {
    color: var(--grey-700);
    margin: 0;
  }
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
